import React, { useCallback } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import EngagementSessionListPageSwitch from "../EngagementSessionListsPage/EngagementSessionListPageSwitch";
import { fetchTopicSessionList, replaceTopicSessionList } from "../../../actions/sessionListsActions";
import { goalLabel } from "../../../constants/goalLabels";

export const TOPIC_SESSION_LIST_DESCRIPTION =
  "The ordered sessions shown in Recommended for people who picked this topic. When someone picks several topics, their lists are interleaved. Drag to reorder, remove with the trash icon, then click Save changes to publish.";

/**
 * The list-editor props for one topic's "Recommended" sessions. Shared by this page and
 * the Edit Topic page, which embeds the same editor. The editor reads ONLY this topic's
 * entry — never another topic's — and must be remounted per slug (`key={slug}`) so
 * switching topics can never show or save the previous topic's working list.
 */
export function useTopicSessionListProps(slug) {
  // Stable per slug: the editor refetches whenever `fetchList` changes identity.
  const selectList = useCallback(state => state.topic_session_list?.[slug], [slug]);
  const fetchList = useCallback(() => fetchTopicSessionList(slug), [slug]);
  const replaceList = useCallback(ids => replaceTopicSessionList(slug, ids), [slug]);
  return { sliceName: "topic_session_list", selectList, fetchList, replaceList };
}

/** The ordered "Recommended" sessions for one topic (route /goal-content/:slug/sessions). */
export default function TopicSessionListPage() {
  const { slug } = useParams();
  // The API label wins; the legacy constant is only a fallback for a row not loaded yet.
  const apiLabel = useSelector(s => (s.goalContent?.goalContents || []).find(c => c.goal_slug === slug)?.label);
  const label = apiLabel || goalLabel(slug);
  const listProps = useTopicSessionListProps(slug);

  return (
    <EngagementSessionListPageSwitch
      key={slug}
      title={`${label} — Recommended sessions`}
      subtitle="Topic"
      description={TOPIC_SESSION_LIST_DESCRIPTION}
      {...listProps}
    />
  );
}
